import { useEffect, useRef, useState } from "react";
import { renderAsync } from "docx-preview";
import CustomLoader from "../../../components/CustomLoader";

interface DocxViewerProps {
  fileSrc: string;
  print?: boolean;
}

const DocxViewer = ({ fileSrc, print = false }: DocxViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const render = async () => {
      if (!fileSrc || !containerRef.current) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(fileSrc);
        const blob = await response.blob();
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = "";
        await renderAsync(blob, containerRef.current, undefined, {
          className: "docx-preview",
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          breakPages: true,
          experimental: true,
        });
      } catch (e) {
        if (!cancelled) {
          setError(
            "Unable to render this Word document. You can still download it using the download icon."
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    render();
    const node = containerRef.current;
    return () => {
      cancelled = true;
      if (node) node.innerHTML = "";
    };
  }, [fileSrc]);

  const containerStyle: React.CSSProperties = print
    ? {
        width: "100%",
        background: "#ffffff",
        pageBreakBefore: "always",
        breakBefore: "page",
      }
    : {
        width: "100%",
        maxWidth: "900px",
        maxHeight: "80vh",
        overflow: "auto",
        background: "#f5f5f5",
        padding: "1rem",
      };

  return (
    <div className="docx-viewer-root" style={containerStyle}>
      {!print && (
        <style>{`
          @media print {
            .docx-viewer-root {
              max-height: none !important;
              overflow: visible !important;
              max-width: none !important;
              padding: 0 !important;
              background: #fff !important;
            }
          }
        `}</style>
      )}
      {isLoading && !print && <CustomLoader />}
      {error && (
        <div style={{ color: "#b91c1c", padding: "1rem", textAlign: "center" }}>
          {error}
        </div>
      )}
      <div ref={containerRef} />
    </div>
  );
};

export default DocxViewer;
